import React, { useCallback, useMemo, useRef, useState } from "react";
import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DownloadIcon from "@mui/icons-material/Download";
import { format } from "date-fns";
import { toast } from "react-toastify";
import type { Message } from "../../../../types";
import { downloadImageProxy } from "../../../../axios/downloadImage";
import {
  getVisualizationMessageUi,
  getVisualizationImages,
  sanitizeVisualizationDownloadBasename,
} from "../../../../helpers/visualizationMessage";
import { writeImageBlobPromiseToClipboard } from "../../../../helpers/writeImageToClipboard";
import { useChatStore } from "../../../../stores/chatStore";
import { useClampedGalleryIndex } from "../../../../hooks/useClampedGalleryIndex";
import { useAuthenticatedFileUrl } from "../../../../hooks/useAuthenticatedFileUrl";
import { CustomMarkdown } from "../../../custom-markdown/custom-markdown";
import { ReactionAndFavorite } from "../reaction-and-favorite/reaction-and-favorite";
import { ImageSwiperModal } from "../image-messages/image-swiper-modal/image-swiper-modal";
import { useVisualizationMessageStyles } from "./styles";
import type { Components } from "react-markdown";

type VisualizationMessageContentProps = {
  message: Message;
  currentReaction?: string;
  isSaved?: boolean;
  markedIds: Record<string, string>;
  isTags?: string[];
  handleReactionClick: (reactionKey: string) => void;
  handleAddToFavorite: () => void;
  handleRemoveTag: (tag: string) => Promise<void>;
};

function extFromMime(mime: string): string {
  if (mime.includes("webp")) return "webp";
  if (mime.includes("jpeg") || mime.includes("jpg")) return "jpg";
  if (mime.includes("gif")) return "gif";
  return "png";
}

export function VisualizationMessageContent({
  message,
  currentReaction,
  isSaved,
  isTags,
  handleReactionClick,
  handleRemoveTag,
  handleAddToFavorite,
  markedIds,
}: VisualizationMessageContentProps) {
  const { setTargetMessageId } = useChatStore();
  const vizImages = useMemo(() => {
    const imgs = getVisualizationImages(message);
    return imgs.length ? [imgs[0]] : [];
  }, [message]);
  const actions = message.visualization?.actions;
  const [busy, setBusy] = useState<"copy" | "download" | null>(null);
  const [isFullScreenOpen, setIsFullScreenOpen] = useState(false);
  const touchStartX = useRef<number | null>(null);
  const suppressImageClickRef = useRef(false);

  const { title, text } = getVisualizationMessageUi(message);
  const replyTargetId =
    message.reply_message_id?.trim() ||
    message.visualization?.source_message_id?.trim() ||
    null;
  const replyTargetTabId = message.reply_message_tab_id?.trim() || null;
  const replyText =
    message.reply_message_text?.trim() || message.reply?.trim() || "";

  const baseFilename = sanitizeVisualizationDownloadBasename(
    message.focus || "visualization",
  );

  const { safeIndex, setCurrentImageIndex } = useClampedGalleryIndex(
    vizImages.length,
  );

  const slide = vizImages[safeIndex];
  const url = slide?.url ?? "";
  const { url: resolvedUrl } = useAuthenticatedFileUrl(url);
  const displayUrl = resolvedUrl || url;

  // Empty description = no side text panel (same as FullScreenImage), more room for zoom/draw.
  const swiperImages = useMemo(
    () =>
      vizImages.map((img) => ({
        url: img.url,
        description: "",
      })),
    [vizImages],
  );

  const slideFilenameSuffix =
    vizImages.length > 1
      ? `_${(safeIndex + 1).toString().padStart(2, "0")}`
      : "";

  const handleDownload = useCallback(async () => {
    if (busy || !slide?.url) return;
    setBusy("download");
    try {
      const mime = slide.mime_type ?? "";
      const ext = extFromMime(mime);
      const filename = `${baseFilename}${slideFilenameSuffix}.${ext}`;
      const blob = await downloadImageProxy(slide.url, filename);
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = filename;
      link.click();
      URL.revokeObjectURL(objectUrl);
      toast.success("Image downloaded");
    } catch {
      toast.error("Could not download image");
    } finally {
      setBusy(null);
    }
  }, [
    baseFilename,
    busy,
    slide?.mime_type,
    slide?.url,
    slideFilenameSuffix,
  ]);

  const handleCopy = useCallback(() => {
    if (busy || !slide?.url) return;
    setBusy("copy");
    const filename = `${baseFilename}${slideFilenameSuffix}.png`;
    writeImageBlobPromiseToClipboard(downloadImageProxy(slide.url, filename))
      .then(() => {
        toast.success("Image copied");
      })
      .catch(() => {
        toast.error("Could not copy image");
      })
      .finally(() => {
        setBusy(null);
      });
  }, [baseFilename, busy, slide?.url, slideFilenameSuffix]);

  const goPrev = useCallback(
    (e?: React.MouseEvent) => {
      e?.stopPropagation();
      if (vizImages.length < 2) return;
      setCurrentImageIndex((i) => (i - 1 + vizImages.length) % vizImages.length);
    },
    [setCurrentImageIndex, vizImages.length],
  );

  const goNext = useCallback(
    (e?: React.MouseEvent) => {
      e?.stopPropagation();
      if (vizImages.length < 2) return;
      setCurrentImageIndex((i) => (i + 1) % vizImages.length);
    },
    [setCurrentImageIndex, vizImages.length],
  );

  const onTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    suppressImageClickRef.current = false;
  };

  const onTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    touchStartX.current = null;
    if (vizImages.length >= 2 && Math.abs(dx) > 40) {
      suppressImageClickRef.current = true;
      if (dx > 40) goPrev();
      else goNext();
    }
  };

  const onGalleryImageClick = () => {
    if (suppressImageClickRef.current) {
      suppressImageClickRef.current = false;
      return;
    }
    setIsFullScreenOpen(true);
  };

  const showCopy =
    actions == null ? true : actions.copy_image === true;
  const showDownload =
    actions == null ? true : actions.download_image === true;
  const showActions = showCopy || showDownload;

  const parsedDate = message.date ? new Date(message.date) : null;
  const showTimestamp = !!parsedDate && !Number.isNaN(parsedDate.getTime());
  const titleMarkdownComponents = useMemo<Components>(
    () => ({
      a: ({ children, ...props }) => (
        <a
          {...props}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: "inherit", textDecoration: "underline" }}
        >
          {children}
        </a>
      ),
    }),
    [],
  );

  const styles = useVisualizationMessageStyles({
    showTimestamp,
    showActions,
  });

  if (!vizImages.length || !url) return null;

  const body = (
    <>
      <Box sx={styles.title}>
        <CustomMarkdown components={titleMarkdownComponents}>
          {title}
        </CustomMarkdown>
      </Box>
      {text ? (
        <Box sx={styles.text}>
          <CustomMarkdown components={titleMarkdownComponents}>
            {text}
          </CustomMarkdown>
        </Box>
      ) : null}
      {replyTargetId ? (
        <Box sx={styles.replyWrap}>
          <Box
            role="button"
            tabIndex={0}
            onClick={(e) => {
              e.stopPropagation();
              setTargetMessageId(replyTargetId, replyTargetTabId);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                e.stopPropagation();
                setTargetMessageId(replyTargetId, replyTargetTabId);
              }
            }}
            sx={styles.replyButton}
          >
            <Box sx={styles.replyAccentBar} />
            <Typography sx={styles.replyTypography}>
              {replyText ? (
                <CustomMarkdown>{replyText}</CustomMarkdown>
              ) : (
                "View original message"
              )}
            </Typography>
          </Box>
        </Box>
      ) : null}
      <Box sx={styles.contentWrap}>
        <Box sx={styles.galleryWrapper}>
          <Box
            sx={styles.galleryImageBox}
            onClick={onGalleryImageClick}
            onTouchStart={onTouchStart}
            onTouchEnd={onTouchEnd}
          >
            <Box
              component="img"
              src={displayUrl}
              alt={message.text || "Visualization"}
              sx={styles.galleryImg}
            />
            {vizImages.length > 1 ? (
              <Box sx={styles.slideBadge} component="span" aria-live="polite">
                {safeIndex + 1} / {vizImages.length}
              </Box>
            ) : null}
            {vizImages.length > 1 ? (
              <>
                <IconButton
                  type="button"
                  size="small"
                  className="prev"
                  aria-label="Previous slide"
                  sx={styles.galleryNav}
                  onClick={goPrev}
                >
                  <NavigateBeforeIcon sx={{ fontSize: 22 }} />
                </IconButton>
                <IconButton
                  type="button"
                  size="small"
                  className="next"
                  aria-label="Next slide"
                  sx={styles.galleryNav}
                  onClick={goNext}
                >
                  <NavigateNextIcon sx={{ fontSize: 22 }} />
                </IconButton>
              </>
            ) : null}
          </Box>
          <ReactionAndFavorite
            currentReaction={currentReaction}
            markedIds={markedIds}
            isSaved={isSaved}
            isTags={isTags}
            handleRemoveTag={handleRemoveTag}
            handleReactionClick={handleReactionClick}
            handleAddToFavorite={handleAddToFavorite}
            reactionBg="#fff"
          />
        </Box>
        {showActions ? (
          <Box sx={styles.actionsRow}>
            {showCopy ? (
              <Tooltip title="Copy image">
                <span>
                  <IconButton
                    size="small"
                    aria-label="Copy image"
                    onClick={handleCopy}
                    disabled={busy !== null}
                    sx={styles.iconButton}
                  >
                    {busy === "copy" ? (
                      <ContentCopyIcon sx={styles.iconBusy} />
                    ) : (
                      <ContentCopyIcon fontSize="small" />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
            ) : null}
            {showDownload ? (
              <Tooltip title="Download image">
                <span>
                  <IconButton
                    size="small"
                    aria-label="Download image"
                    onClick={handleDownload}
                    disabled={busy !== null}
                    sx={styles.iconButton}
                  >
                    {busy === "download" ? (
                      <DownloadIcon sx={styles.iconBusy} />
                    ) : (
                      <DownloadIcon fontSize="small" />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
            ) : null}
          </Box>
        ) : null}
        {showTimestamp && parsedDate ? (
          <Typography variant="caption" sx={styles.timestamp}>
            <span>{format(parsedDate, "dd/MM/yy")}</span>
            <Box component="span" sx={styles.timestampSpacer} />
            <span>{format(parsedDate, "HH:mm")}</span>
          </Typography>
        ) : null}
      </Box>
    </>
  );

  return (
    <Box sx={styles.root}>
      <Box sx={styles.gradientOuter}>
        <Box sx={styles.innerCard}>{body}</Box>
      </Box>
      <ImageSwiperModal
        open={isFullScreenOpen}
        onClose={() => setIsFullScreenOpen(false)}
        images={swiperImages}
        initialIndex={safeIndex}
      />
    </Box>
  );
}
